import {
    ALL_EMAIL_FREQUENCY_OF_REMINDERS,
    EMAIL_FREQUENCY_OF_REMINDER_LABELS,
} from '@common/enums';
import InfoIcon from '@mui/icons-material/Info';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    AlertTitle,
    Box,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    type SxProps,
    TextField,
    type Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { useState } from 'react';
import AutoCompleteMultiSelect from '@/components/AutoCompleteMultiselect';
import { InfoSolid } from '@/components/Icons/SystemIcons';
import LexoSwitch from '@/components/LexoSwitch';
import { REMINDER_TIME_OPTIONS } from '@/pages/QuoteIntake/constants';
import useQuoteIntakeSetupStore from '@/stores/useQuoteIntakeSetupStore';
import { infoSnackbar } from '@/utils/snackbar';

const formItemsGroupContainerStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
};

const formItemContainerStyle: SxProps<Theme> = {
    display: 'flex',
    position: 'relative',
};

export default function IntakeEmailTemplateForm() {
    const theme = useTheme();
    const [formData, availableCcContacts, errorData, setFormData, setErrorData] =
        useQuoteIntakeSetupStore((state) => [
            state.formData,
            state.availableCcContacts,
            state.errorData,
            state.setFormData,
            state.setErrorData,
        ]);
    const [showTip, setShowTip] = useState(true);

    //Styles - static
    const intakeEmailSectionsStyle: SxProps<Theme> = {
        borderRadius: '8px',
        border: `1px solid ${theme.palette.neutral.borderColor}`,
        backgroundColor: 'neutral.white',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        height: 'fit-content',
        width: '100%',
    };

    const handleStateChange = (event: any, key: string) => {
        setErrorData({
            ...errorData,
            step5: {
                intakeEmailTemplateError: {
                    emailSubject: undefined,
                    emailSignature: undefined,
                    emailBodyClosing: undefined,
                    emailBodyIntroduction: undefined,
                },
                intakeReminderTemplateErrors: {
                    emailSubject: undefined,
                    emailSignature: undefined,
                    emailBodyClosing: undefined,
                    emailBodyIntroduction: undefined,
                },
            },
        });
        setFormData('step5', {
            ...formData.step5,
            [key]: event.target.value,
        });
    };

    const handleCcChange = (_event, value: string[]) => {
        const ev = { target: { value: value } };
        const key = formData.step5.isScreenTwo ? 'reminderContactsCC' : 'contactCC';
        handleStateChange(ev, key);
    };

    const errorKey = formData.step5.isScreenTwo
        ? 'intakeReminderTemplateErrors'
        : 'intakeEmailTemplateError';

    //JSX
    const reminderAccordion = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
            }}
        >
            <Typography variant="body2">Enable intake reminders</Typography>
            <LexoSwitch
                checked={formData.step5.isQuoteReminderOn}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const ev = { target: { value: event.target.checked } };
                    if (formData.step5.isQuoteReminderOn) {
                        infoSnackbar('Reminders are currently set to off');
                    }
                    handleStateChange(ev, 'isQuoteReminderOn');
                }}
            />
            <Accordion expanded={formData.step5.isQuoteReminderOn}>
                <AccordionSummary>
                    <Typography
                        variant={'labelLarge'}
                        color={
                            formData.step5.isQuoteReminderOn
                                ? theme.palette.neutral.black
                                : theme.palette.neutral.gray
                        }
                    >
                        Reminders frequency
                    </Typography>
                </AccordionSummary>
                <AccordionDetails
                    sx={{
                        '&.MuiAccordionDetails-root': {
                            paddingTop: '0px',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Divider orientation="horizontal" />
                        <FormControl fullWidth>
                            <InputLabel id="reminder-count-label">
                                Total number of intake reminders
                            </InputLabel>
                            <Select
                                value={formData.step5.totalNumberOfReminders}
                                onChange={(event) =>
                                    handleStateChange(event, 'totalNumberOfReminders')
                                }
                                disabled={!formData.step5.isQuoteReminderOn}
                                labelId="reminder-count-label"
                                label="Total number of intake reminders"
                            >
                                <MenuItem value={1}>1</MenuItem>
                                <MenuItem value={2}>2</MenuItem>
                                <MenuItem value={3}>3</MenuItem>
                                <MenuItem value={4}>4</MenuItem>
                                <MenuItem value={5}>5</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="reminder-frequency-label">
                                Frequency of intake reminders
                            </InputLabel>
                            <Select
                                value={formData.step5.emailFrequencyOfReminder}
                                onChange={(event: SelectChangeEvent<unknown>) =>
                                    handleStateChange(event, 'emailFrequencyOfReminder')
                                }
                                disabled={!formData.step5.isQuoteReminderOn}
                                labelId="reminder-frequency-label"
                                label="Frequency of intake reminders"
                            >
                                {ALL_EMAIL_FREQUENCY_OF_REMINDERS.map((frequency) => (
                                    <MenuItem key={frequency} value={frequency}>
                                        {EMAIL_FREQUENCY_OF_REMINDER_LABELS[frequency]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="reminder-time-label">Reminders time</InputLabel>
                            <Select
                                value={formData.step5.reminderTime}
                                onChange={(event: SelectChangeEvent<unknown>) =>
                                    handleStateChange(event, 'reminderTime')
                                }
                                disabled={!formData.step5.isQuoteReminderOn}
                                labelId="reminder-time-label"
                                label="Reminders time"
                            >
                                {Object.entries(REMINDER_TIME_OPTIONS).map(([key, value]) => (
                                    <MenuItem key={key} value={value}>
                                        {key}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
    return (
        <Box sx={{ ...intakeEmailSectionsStyle, margin: '32px 0px 32px 32px' }}>
            <Box>
                <Typography variant="h2">
                    {formData.step5.isScreenTwo
                        ? 'Intake email reminder template'
                        : 'Intake email template'}
                </Typography>
                <Typography variant="body2" sx={{ marginTop: '8px' }}>
                    {formData.step5.isScreenTwo
                        ? 'When your client accepts a quote, they will be automatically directed to the intake form. An email will be sent to them after acceptance, according to the reminder schedule you have set for this email.'
                        : 'Customize your email template and see changes on preview on the right.'}
                </Typography>
                {!formData.step5.isScreenTwo && (
                    <FormControl fullWidth sx={{ marginTop: '8px' }}>
                        <TextField
                            value={formData.step5.expiry || 180}
                            onChange={(ev) => handleStateChange(ev, 'expiry')}
                            fullWidth
                            placeholder="Quote Request Response"
                            type="text"
                            variant="outlined"
                            label="Number of days before intake expires"
                            name="intake-expiry-count"
                            disabled
                        />

                        {showTip && (
                            <Alert
                                icon={<InfoSolid />}
                                severity="info"
                                onClose={() => {
                                    setShowTip(false);
                                }}
                                sx={{
                                    background: theme.components?.MuiAlert?.styleOverrides,
                                    mt: 2,
                                }}
                            >
                                <AlertTitle>Number of days</AlertTitle>
                                This field is currently not editable
                            </Alert>
                        )}
                    </FormControl>
                )}
            </Box>
            <Divider orientation="horizontal" />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                }}
            >
                {formData.step5.isScreenTwo && (
                    <>
                        {reminderAccordion}
                        <Divider orientation="horizontal" />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            <Typography variant="body2">
                                Notify via email when the maximum number of reminders has been sent
                                to the client.
                            </Typography>
                            <LexoSwitch
                                checked={formData.step5.enableReminderExhaustNotification}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const ev = { target: { value: event.target.checked } };
                                    handleStateChange(ev, 'enableReminderExhaustNotification');
                                }}
                            />
                        </Box>
                    </>
                )}
            </Box>
            {formData.step5.isScreenTwo && <Divider orientation="horizontal" />}
            <Box sx={formItemsGroupContainerStyle}>
                <Box sx={formItemContainerStyle}>
                    <TextField
                        value={
                            formData.step5.isScreenTwo
                                ? formData.step5.reminderSubject
                                : formData.step5.subject
                        }
                        onChange={(ev) => handleStateChange(ev, ev.target.name)}
                        fullWidth
                        placeholder="Intake Email Reminder"
                        name={formData.step5.isScreenTwo ? 'reminderSubject' : 'subject'}
                        error={!!errorData.step5[errorKey]?.emailSubject}
                        helperText={errorData.step5[errorKey]?.emailSubject}
                        type="text"
                        variant="outlined"
                        label="Subject"
                    />
                </Box>
                <Box sx={formItemContainerStyle}>
                    <TextField
                        sx={{
                            '& .MuiInputBase-root': {
                                height: 'initial',
                            },
                        }}
                        value={
                            formData.step5.isScreenTwo
                                ? formData.step5.reminderBodyBeforeLink
                                : formData.step5.bodyBeforeLink
                        }
                        onChange={(ev) => handleStateChange(ev, ev.target.name)}
                        name={
                            formData.step5.isScreenTwo ? 'reminderBodyBeforeLink' : 'bodyBeforeLink'
                        }
                        fullWidth
                        placeholder=""
                        type="text"
                        multiline={true}
                        rows={4}
                        error={!!errorData.step5[errorKey]?.emailBodyIntroduction}
                        helperText={
                            errorData.step5[errorKey]?.emailBodyIntroduction
                                ? 'Email body introduction is required.'
                                : ''
                        }
                        variant="outlined"
                        label="First part of body (before form link)"
                    />
                </Box>
                <Box sx={formItemContainerStyle}>
                    <TextField
                        sx={{
                            '& .MuiInputBase-root': {
                                height: 'initial',
                            },
                        }}
                        value={
                            formData.step5.isScreenTwo
                                ? formData.step5.reminderBodyAfterLink
                                : formData.step5.bodyAfterLink
                        }
                        onChange={(ev) => handleStateChange(ev, ev.target.name)}
                        name={
                            formData.step5.isScreenTwo ? 'reminderBodyAfterLink' : 'bodyAfterLink'
                        }
                        fullWidth
                        placeholder=""
                        type="text"
                        multiline={true}
                        rows={4}
                        error={!!errorData.step5[errorKey]?.emailBodyClosing}
                        helperText={
                            errorData.step5[errorKey]?.emailBodyClosing
                                ? 'Email body closing is required.'
                                : ''
                        }
                        variant="outlined"
                        label="Second part of body (after form link)"
                    />
                </Box>
                {!formData.step5.isScreenTwo && (
                    <Box sx={{ ...formItemContainerStyle, flexDirection: 'column' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: '4px',
                                alignItems: 'center',
                                marginBottom: '16px',
                            }}
                        >
                            <Typography variant="labelMedium" color={theme.palette.neutral.black}>
                                Contacts to CC
                            </Typography>
                            <Tooltip
                                arrow
                                placement="top-start"
                                title={
                                    <Box>
                                        <Typography
                                            variant="h4"
                                            sx={{ color: theme.palette.neutral.black }}
                                        >
                                            Users included in this section will be CC'ed on the
                                            following notifications:
                                        </Typography>
                                        <br />
                                        <Typography variant="body2">
                                            <ul>
                                                <li>Intake complete notification</li>
                                            </ul>
                                        </Typography>
                                    </Box>
                                }
                            >
                                <IconButton sx={{ padding: '0px' }}>
                                    <InfoIcon sx={{ fontSize: '16px' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <AutoCompleteMultiSelect
                            fullWidth
                            getOptionLabel={(options: any) => {
                                return options;
                            }}
                            label=""
                            value={
                                formData.step5.isScreenTwo
                                    ? formData.step5.reminderContactsCC
                                    : formData.step5.contactCC
                            }
                            options={availableCcContacts}
                            onChange={handleCcChange}
                            limitTags={3} // limits number of chip to render while out of focus, useful for responsiveness
                            getLimitTagsText={(count: any) => `+${count}📦`} // modify the limit tag text, useful for translation too
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}
